/**
 * The status pill, and the determinate bar it sometimes carries.
 *
 * `<MarketStatusPill>` (Phase 4) is this with the D6 state machine wired to it;
 * the tone table and the bar are here because `STALE`, `PENDING`, tab counts and
 * the connection banner all need the same object.
 *
 * **The bar is determinate on purpose** (components.md §2.5). `FIXING` and
 * `RESOLVING` both last a known, server-published interval — ten seconds and
 * about thirteen respectively, and since register §F24 they are *different*
 * intervals rather than the same one — and an indeterminate shimmer over a
 * bounded window reads as "something is wrong" when nothing is. `progress` is
 * therefore a number the caller computes from the server's own deadline
 * (`open_at_ms`, `resolve_eta_ms`) — never a hardcoded animation, which is why
 * this component takes a fraction and owns no timer.
 *
 * Passing `progress={null}` is the one case the design allows an indeterminate
 * bar: `resolve_eta_ms` has passed with no `RESOLVED`, where inventing progress
 * the server has not reported would be a lie.
 */

import type { ReactNode } from 'react'
import { cx } from '@/lib/cx'

export type PillTone =
  | 'neutral'
  | 'up'
  | 'down'
  | 'accent'
  | 'info'
  | 'warning'
  | 'danger'
  | 'success'
  | 'reward'

const TONE: Record<PillTone, { chip: string; bar: string }> = {
  neutral: { chip: 'bg-bg-raised text-text-secondary', bar: 'bg-border-strong' },
  up: { chip: 'bg-up-bg text-up-text', bar: 'bg-up-solid' },
  down: { chip: 'bg-down-bg text-down-text', bar: 'bg-down-solid' },
  accent: { chip: 'bg-accent-bg text-accent-text', bar: 'bg-accent-solid' },
  info: { chip: 'bg-info-bg text-info-text', bar: 'bg-info-solid' },
  warning: { chip: 'bg-warning-bg text-warning-text', bar: 'bg-warning-solid' },
  danger: { chip: 'bg-danger-bg text-danger-text', bar: 'bg-danger-solid' },
  success: { chip: 'bg-success-bg text-success-text', bar: 'bg-success-solid' },
  reward: { chip: 'bg-reward-bg text-reward-text', bar: 'bg-reward-solid' },
}

export interface PillProps {
  tone?: PillTone
  /** The 6px pulsing dot of the `OPEN` pill. */
  dot?: boolean
  /**
   * 0–1 for the determinate bar; `null` for indeterminate; omitted for no bar.
   * Computed by the caller from the server's deadline, never from a timer here.
   */
  progress?: number | null
  /** Dimmed and desaturated — the `STALE` treatment. */
  muted?: boolean
  className?: string
  children: ReactNode
}

export function Pill({ tone = 'neutral', dot, progress, muted, className, children }: PillProps) {
  const t = TONE[tone]
  const hasBar = progress !== undefined

  return (
    <span
      data-tone={tone}
      className={cx(
        'relative inline-flex h-[var(--ud-control-height-sm)] items-center gap-1_5 overflow-hidden rounded-sm px-1_5 text-caption font-medium whitespace-nowrap',
        t.chip,
        muted && 'opacity-55 saturate-50',
        className,
      )}
    >
      {dot ? (
        <span
          aria-hidden="true"
          className={cx('inline-block size-1_5 shrink-0 animate-pulse rounded-full', t.bar)}
        />
      ) : null}
      {children}
      {hasBar ? (
        <span
          aria-hidden="true"
          className="absolute inset-x-0 bottom-0 h-0_5 bg-bg-active"
          data-progress={progress === null ? 'indeterminate' : progress.toFixed(3)}
        >
          <span
            className={cx(
              'block h-full',
              t.bar,
              progress === null && 'w-1/3 animate-pulse',
            )}
            style={
              progress === null
                ? undefined
                : { width: `${Math.min(100, Math.max(0, progress * 100))}%` }
            }
          />
        </span>
      ) : null}
    </span>
  )
}
